import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, AlertCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import ExpertSection from "@/components/ExpertSection";
import CTASection from "@/components/CTASection";
import TariffBreadcrumbs from "@/components/tarif/TariffBreadcrumbs";
import FAQSection from "@/components/tarif/FAQSection";
import DocumentDownloads from "@/components/tarif/DocumentDownloads";
import ThreeStepsProtection from "@/components/tarif/ThreeStepsProtection";
import TimelineSection from "@/components/tarif/TimelineSection";
import StickyCTA from "@/components/tarif/StickyCTA";
import StructuredData from "@/components/StructuredData";
import HeroSection from "@/components/layout/HeroSection";
import { LucideIcon } from "lucide-react";
import tarifHero from "@/assets/tarif-hero.jpg";
import { getInsuranceProductSchema } from "@/utils/seoSchemas";

interface HeroFeature {
  icon: LucideIcon;
  title: string;
  subtitle: string;
}

interface CoverageArea {
  icon: LucideIcon;
  title: string;
  items: string[];
}

interface TargetGroup {
  icon: LucideIcon;
  title: string;
  description: string;
  benefits: string[];
  recommendation: string;
}

interface InsuranceCondition {
  icon: LucideIcon;
  title: string;
  description: string | string[];
}

interface FAQ {
  question: string;
  answer: string;
}

interface Document {
  title: string;
  type: string;
  url: string;
}

interface BaseTariffPageProps {
  // Company Branding
  companyName: string;
  companyColor: string;
  
  // SEO & Meta
  pageTitle: string;
  metaDescription: string;
  metaKeywords: string;
  canonicalUrl: string;
  
  // Hero Section
  heroLabel: string;
  heroLabelIcon?: LucideIcon;
  heroTitle: string | React.ReactNode;
  heroDescription: string;
  heroFeatures: HeroFeature[];
  
  // Intro Alert
  alertTitle: string;
  alertDescription: string;
  
  // Coverage Areas (4 Karten)
  coverageAreas: CoverageArea[];
  
  // Target Groups (4 Gruppen)
  targetGroups: TargetGroup[];
  
  // Insurance Conditions (6 Items)
  insuranceConditions: InsuranceCondition[];
  
  // FAQ Section
  faqs: FAQ[];
  
  // Document Downloads (2 Dokumente)
  documents: Document[];
  
  // Product Schema (Optional - für Bewertungen)
  productRating?: {
    value: number;
    count: number;
  };
  priceRange?: string;
}

export const BaseTariffPage = ({
  companyName,
  companyColor,
  pageTitle,
  metaDescription,
  metaKeywords,
  canonicalUrl,
  heroLabel,
  heroLabelIcon,
  heroTitle,
  heroDescription,
  heroFeatures,
  alertTitle,
  alertDescription,
  coverageAreas,
  targetGroups,
  insuranceConditions,
  faqs,
  documents,
  productRating,
  priceRange,
}: BaseTariffPageProps) => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://praxis-versichert.de"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Tarifwissen",
        "item": "https://praxis-versichert.de/tarifwissen"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `${companyName} Cyberversicherung`
      }
    ]
  };

  // Product Schema für Sternebewertungen in SERPs
  const productSchema = getInsuranceProductSchema({
    name: `${companyName} Cyberversicherung für Arztpraxen`,
    description: metaDescription,
    url: canonicalUrl,
    brand: companyName,
    rating: productRating,
    priceRange: priceRange,
    image: "https://praxis-versichert.de/logo.png"
  });

  const schemas = [breadcrumbSchema, productSchema];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      
      <StructuredData data={schemas} />
      
      <Navigation />
        
          <main className="flex-grow">
            {/* Hero Section with fade-bottom */}
            <HeroSection
              image={tarifHero}
              alt={`${companyName} Cyberversicherung für Arztpraxen`}
              title={`${companyName} Cyberversicherung – Professioneller Schutz für medizinische Einrichtungen`}
              gradientPreset="none"
              imageOpacity={30}
              overlayColor="background"
              className="pt-20 sm:pt-24 pb-12 sm:pb-16 lg:pb-20"
            >
              <div itemScope itemType="https://schema.org/Service">
                <meta itemProp="serviceType" content="Cyberversicherung" />
                <meta itemProp="provider" content="Praxis Versichert" />
                <meta itemProp="areaServed" content="Deutschland" />
                
                <TariffBreadcrumbs companyName={companyName} />
                
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
                  <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 bg-card/80 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-border/50 mb-4 sm:mb-6">
                      {heroLabelIcon && (() => {
                        const Icon = heroLabelIcon;
                        return <Icon className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: companyColor }} />;
                      })()}
                      <span className="text-[10px] sm:text-xs font-medium" style={{ color: companyColor }}>
                        {heroLabel}
                      </span>
                    </div>
                    
                    <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4 lg:mb-6 bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent" itemProp="name">
                      {heroTitle}
                    </h1>
                    
                    <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto" itemProp="description">
                      {heroDescription}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12">
                      <Button 
                        asChild 
                        size="lg" 
                        className="w-full sm:w-auto text-sm sm:text-base group shadow-lg hover:shadow-xl transition-all duration-300 hover:opacity-90"
                        style={{ 
                          backgroundColor: companyColor,
                          color: '#FFFFFF'
                        }}
                      >
                        <Link to="/termin-buchen">
                          Jetzt beraten lassen
                          <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                      <Button 
                        asChild 
                        variant="outline" 
                        size="lg"
                        className="w-full sm:w-auto text-sm sm:text-base border-2
                          bg-transparent
                          hover:!bg-transparent
                          hover:!border-opacity-80
                          hover:!shadow-[0_0_15px_rgba(0,0,0,0.3)]
                          transition-all duration-300"
                        style={{ 
                          borderColor: companyColor,
                          color: companyColor
                        }}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget as HTMLElement;
                          target.style.borderColor = companyColor;
                          target.style.color = companyColor;
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget as HTMLElement;
                          target.style.borderColor = companyColor;
                          target.style.color = companyColor;
                        }}
                      >
                        <Link to="/tarifwissen">Alle Tarife vergleichen</Link>
                      </Button>
                    </div>

                    {/* Key Features */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
                      {heroFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3 bg-card/50 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-border/50">
                          <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: companyColor }} />
                          <div className="text-left">
                            <div className="font-semibold text-xs sm:text-sm">{feature.title}</div>
                            <div className="text-[10px] sm:text-xs text-muted-foreground">{feature.subtitle}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </HeroSection>

            {/* Intro Alert */}
            <section className="py-8 sm:py-12">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <Alert className="max-w-4xl mx-auto border-l-4" style={{ borderLeftColor: companyColor }}>
                  <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: companyColor }} />
                  <AlertDescription className="text-sm sm:text-base ml-2">
                    <strong>{alertTitle}</strong> {alertDescription}
                  </AlertDescription>
                </Alert>
              </div>
            </section>

            {/* Coverage Areas - 4er Grid */}
            <section className="py-16 sm:py-20 lg:py-24">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                    Umfassender Versicherungsschutz
                  </h2>
                  <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                    Die {companyName} Cyberversicherung schützt Ihre Praxis vor allen relevanten Cyber-Risiken
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
                  {coverageAreas.map((area, index) => {
                    const Icon = area.icon;
                    return (
                      <Card 
                        key={index} 
                        className="group relative overflow-hidden
                          bg-white/5
                          border border-white/10
                          rounded-lg
                          shadow-sm
                          hover:shadow-md
                          hover:border-white/20
                          transition-all duration-300 ease-out"
                      >
                        <div className="relative z-10">
                          <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
                            <div className="flex flex-col items-center gap-2 sm:gap-3">
                              <div className="flex items-center justify-center flex-shrink-0">
                                <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-white/70" />
                              </div>
                              <CardTitle className="text-lg sm:text-xl text-center text-white">
                                {area.title}
                              </CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent className="px-3 sm:px-4 lg:px-5 pb-3 sm:pb-4 lg:pb-5 pt-2">
                            <ul className="space-y-2 sm:space-y-3">
                              {area.items.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="text-white/50 mt-1 flex-shrink-0">•</span>
                                  <span className="text-xs sm:text-sm text-white/70 leading-relaxed">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Target Groups - 4er Grid mit Gradient-Überschrift */}
            <section className="py-16 sm:py-20 lg:py-24">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8">
                    <span className="block text-foreground">Für wen ist die</span>
                    <span 
                      className="inline-block bg-clip-text text-transparent py-1"
                      style={{ 
                        backgroundImage: `linear-gradient(to right, ${companyColor}, ${companyColor}dd, ${companyColor})`,
                        textShadow: `0 0 40px ${companyColor}40, 0 0 80px ${companyColor}20`
                      }}
                    >
                      {companyName} Cyberversicherung
                    </span>
                    <span className="block text-foreground mt-3">geeignet?</span>
                  </h2>
                  <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mt-6">
                    Maßgeschneiderter Schutz für jede Praxisgröße
                  </p>
                </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
                  {targetGroups.map((group, index) => {
                    const Icon = group.icon;
                    return (
                      <Card 
                        key={index} 
                        className="group relative overflow-hidden
                          bg-white/5
                          border border-white/10
                          rounded-lg
                          shadow-sm
                          hover:shadow-md
                          hover:border-white/20
                          transition-all duration-300 ease-out"
                      >
                        <div className="relative z-10">
                          <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${companyColor}15` }}>
                <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: companyColor }} />
              </div>
              <div>
                <CardTitle className="text-lg sm:text-xl mb-2">{group.title}</CardTitle>
                <p className="text-xs sm:text-sm text-muted-foreground">{group.description}</p>
              </div>
            </div>
                          </CardHeader>
                          <CardContent className="px-3 sm:px-4 lg:px-5 pb-3 sm:pb-4 lg:pb-5 pt-2 space-y-4">
                            <ul className="space-y-2">
                              {group.benefits.map((benefit, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="text-white/50 mt-1 flex-shrink-0">•</span>
                                  <span className="text-xs sm:text-sm text-white/70 leading-relaxed">{benefit}</span>
                                </li>
                              ))}
                            </ul>
                            <div className="pt-3 border-t border-white/10">
                              <p className="text-xs sm:text-sm font-semibold" style={{ color: companyColor }}>
                                {group.recommendation}
                              </p>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Versicherungskonditionen - 3x2 Grid */}
            <section className="py-16 sm:py-20 lg:py-24">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                    Versicherungskonditionen im Überblick
                  </h2>
                  <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                    Transparente Bedingungen und flexible Gestaltungsmöglichkeiten
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-7xl mx-auto">
                  {insuranceConditions.map((condition, index) => {
                    const Icon = condition.icon;
                    return (
                      <Card 
                        key={index} 
                        className="group relative overflow-hidden
                          bg-white/5
                          border border-white/10
                          rounded-lg
                          shadow-sm
                          hover:shadow-md
                          hover:border-white/20
                          transition-all duration-300 ease-out"
                      >
                        <div className="relative z-10">
                          <CardHeader className="p-3 sm:p-4 lg:p-5 pb-2">
                            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-2 sm:gap-3">
                              <div className="flex items-center justify-center flex-shrink-0">
                                <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-white/70" />
                              </div>
                              <CardTitle className="text-lg sm:text-xl text-center lg:text-left text-white">
                                {condition.title}
                              </CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent className="px-3 sm:px-4 lg:px-5 pb-3 sm:pb-4 lg:pb-5 pt-2">
                            {Array.isArray(condition.description) ? (
                              <ul className="space-y-2">
                                {condition.description.map((item, idx) => (
                                  <li key={idx} className="flex items-start gap-2">
                                    <span className="text-white/50 mt-1 flex-shrink-0">•</span>
                                    <span className="text-xs sm:text-sm text-white/70 leading-relaxed">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs sm:text-sm text-white/70 leading-relaxed">{condition.description}</p>
                            )}
                          </CardContent>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Timeline Section */}
            <TimelineSection companyName={companyName} companyColor={companyColor} />

            {/* FAQ Section */}
            <FAQSection questions={faqs} companyName={companyName} />

            {/* Document Downloads */}
            <DocumentDownloads documents={documents} companyName={companyName} />

            {/* Three Steps Protection */}
            <ThreeStepsProtection companyColor={companyColor} companyName={companyName} />

            {/* Expert Section */}
            <ExpertSection />

            {/* CTA Section */}
            <CTASection />

            {/* Sticky CTA */}
            <StickyCTA companyName={companyName} companyColor={companyColor} />
          </main>

          <Footer />
    </>
  );
};
